import React, { useState, useEffect } from 'react';
import { Agency } from './AgencyManagementPage';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Switch, FormControlLabel, List, ListItem, ListItemText, Checkbox, Paper, Typography, IconButton, Box
} from '@mui/material';
import { SelectAll, Deselect } from '@mui/icons-material';

interface AgencyFormModalProps {
    agency: Agency | null;
    agencies: Agency[];
    onClose: () => void;
    onSave: (agency: Agency) => void;
}

const AgencyFormModal: React.FC<AgencyFormModalProps> = ({ agency, agencies, onClose, onSave }) => {
    const [formData, setFormData] = useState<Agency>(
        agency || { name: '', ori: '', active: true, authorized_oris: [], type: 'AGENCY' }
    );
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (agency) {
            setFormData(agency);
        }
    }, [agency]);

    const handleToggle = (ori: string) => () => {
        const currentIndex = formData.authorized_oris.indexOf(ori);
        const newAuthorizedOris = [...formData.authorized_oris];

        if (currentIndex === -1) {
            newAuthorizedOris.push(ori);
        } else {
            newAuthorizedOris.splice(currentIndex, 1);
        }

        setFormData({ ...formData, authorized_oris: newAuthorizedOris });
    };

    const handleSave = () => {
        onSave(formData);
    };

    const availableOris = agencies
        .filter(a => a.name && a.ori && a.ori !== agency?.ori)
        .sort((a, b) => a.name.localeCompare(b.name));

    const filteredOris = availableOris.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ori.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelectAll = () => {
        const newAuthorizedOris = [...new Set([...formData.authorized_oris, ...filteredOris.map(o => o.ori)])];
        setFormData({ ...formData, authorized_oris: newAuthorizedOris });
    };

    const handleDeselectAll = () => {
        const filteredOrisOric = new Set(filteredOris.map(o => o.ori));
        const newAuthorizedOris = formData.authorized_oris.filter(ori => !filteredOrisOric.has(ori));
        setFormData({ ...formData, authorized_oris: newAuthorizedOris });
    };

    return (
        <Dialog open onClose={(event, reason) => {
            if (reason !== 'backdropClick') {
                onClose();
            }
        }} disableEscapeKeyDown fullWidth maxWidth="sm">
            <DialogTitle>{agency ? 'Edit Agency' : 'Add Agency'}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    name="name"
                    label="Agency Name"
                    type="text"
                    fullWidth
                    variant="standard"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <TextField
                    margin="dense"
                    name="ori"
                    label="ORI"
                    type="text"
                    fullWidth
                    variant="standard"
                    value={formData.ori}
                    disabled={!!agency}
                    onChange={(e) => setFormData({ ...formData, ori: e.target.value })}
                />
                <FormControlLabel
                    control={<Switch checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} name="active" />}
                    label="Active"
                />
                <Typography sx={{ mt: 2, mb: 1 }} variant="h6">Authorized ORIs</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                        label="Search Authorized ORIs"
                        variant="outlined"
                        size="small"
                        fullWidth
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <IconButton onClick={handleSelectAll} title="Select All Filtered">
                        <SelectAll />
                    </IconButton>
                    <IconButton onClick={handleDeselectAll} title="Deselect All Filtered">
                        <Deselect />
                    </IconButton>
                </Box>
                <Paper sx={{ height: 300, overflow: 'auto', mt: 1 }}>
                    <List dense>
                        {filteredOris.map((value: Agency) => {
                            const labelId = `checkbox-list-label-${value.ori}`;
                            return (
                                <ListItem
                                    key={value.ori}
                                    secondaryAction={
                                        <Checkbox
                                            edge="end"
                                            onChange={handleToggle(value.ori)}
                                            checked={formData.authorized_oris.indexOf(value.ori) !== -1}
                                            inputProps={{ 'aria-labelledby': labelId }}
                                        />
                                    }
                                    disablePadding
                                >
                                    <ListItemText id={labelId} primary={value.name} secondary={value.ori} />
                                </ListItem>
                            );
                        })}
                    </List>
                </Paper>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AgencyFormModal;
